import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';

/**
 * Informative placeholder route (PRO-0250, D6; reskinned PRO-0260 Phase 1;
 * generalised PRO-0340, T5). Renders an explicit, focusable, announced empty
 * state — a heading + `Card` — never blank, never a 404. Used both for landed-
 * later Comms areas (`/messages`, `/inbox`) and for the inert "coming soon"
 * Human Resources / Finance module surfaces.
 *
 * Back-compat: callers passing only `title` keep the original Comms copy and no
 * badge, so the existing `/messages` `/inbox` placeholders are unchanged. The
 * `comingSoon` badge + an optional `roadmapHref` affordance are opt-in for the
 * future-state modules (D4). The heading is the focus target for `useRouteTitle`
 * (NF-10) — including the module index routes, which is why they render this
 * overview placeholder rather than a bare redirect (MF-001).
 */
const DEFAULT_DESCRIPTION =
  'This area is part of the Unified Communications workspace and will become available in a later wave.';

export interface ModulePlaceholderProps {
  /** Sub-module / area name — matches the wiki taxonomy exactly. */
  title: string;
  /**
   * One-line "what this will do" (non-PII, static). Defaults to the Comms copy
   * when omitted/undefined (back-compat); `undefined` is accepted explicitly so
   * a registry-sourced `summary` can be threaded through under
   * `exactOptionalPropertyTypes`.
   */
  description?: string | undefined;
  /** When true, render the "Coming soon" badge (HR/Finance future-state modules). */
  comingSoon?: boolean;
  /** Optional roadmap link affordance (e.g. wiki module page). */
  roadmapHref?: string | undefined;
  /** Shown below summary on coming-soon enriched placeholders (PRO-0480, PRD D5). */
  planningNote?: string | undefined;
}

export function ModulePlaceholder({
  title,
  description = DEFAULT_DESCRIPTION,
  comingSoon = false,
  roadmapHref,
  planningNote,
}: ModulePlaceholderProps) {
  return (
    <section aria-labelledby="placeholder-heading">
      <h1 id="placeholder-heading" className="mb-4 text-xl font-bold text-text-primary">
        {title}
      </h1>
      <Card className="max-w-narrow p-6">
        {comingSoon ? <Badge state="upcoming">Coming soon</Badge> : null}
        <p className={`m-0 text-base text-text-secondary${comingSoon ? ' mt-3' : ''}`}>{description}</p>
        {planningNote ? (
          <p className="mt-3 m-0 text-sm text-text-secondary">{planningNote}</p>
        ) : null}
        {roadmapHref ? (
          <a
            href={roadmapHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Read more about ${title} on the wiki`}
            className="mt-3 inline-flex min-h-11 items-center text-base font-semibold text-primary"
          >
            Read more
          </a>
        ) : null}
      </Card>
    </section>
  );
}
